<template>
  <div class="px-6 pb-6">
    <v-card class="rounded-lg px-6 py-6" elevation="0" style="border: 1px solid #e0e0e0;">

      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-6 flex-wrap">
        <div>
          <h2 class="text-h5 font-weight-bold grey--text text--darken-3 mb-1">Библиотека кейсов</h2>
          <div class="text-body-2 grey--text">Всего: {{ filteredCases.length }}</div>
        </div>
        <v-btn
          color="#009E60"
          dark
          depressed
          class="text-capitalize mt-2 mt-sm-0 px-6"
          height="40"
          @click="openCaseModal(null)"
        >
          <v-icon left small>mdi-plus</v-icon>
          Новый кейс
        </v-btn>
      </div>

      <!-- Filters -->
      <div class="d-flex mb-4" style="gap: 12px; flex-wrap: wrap;">
        <v-text-field
          v-model="search"
          outlined
          dense
          hide-details
          placeholder="Поиск по названию или описанию"
          prepend-inner-icon="mdi-magnify"
          style="max-width: 560px; flex: 1;"
          class="lc-search"
        />
        <v-select
          v-model="filterCompetence"
          :items="competenceOptions"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details
          style="max-width: 260px; min-width: 180px;"
          class="lc-select"
        />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="d-flex justify-center py-10">
        <v-progress-circular indeterminate color="#009E60" />
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredCases.length" class="d-flex flex-column align-center py-10">
        <v-icon size="48" color="grey lighten-2">mdi-folder-open-outline</v-icon>
        <p class="text-body-2 grey--text mt-3">Нет кейсов. Нажмите «Новый кейс».</p>
      </div>

      <!-- List -->
      <div v-else class="lc-list">
        <div
          v-for="item in filteredCases"
          :key="item.id"
          class="lc-row"
        >
          <div class="lc-row__main">
            <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px;">
              <span class="lc-row__name">{{ item.name }}</span>
              <span v-if="competenceName(item.competence)" class="lc-chip">
                {{ competenceName(item.competence) }}
              </span>
            </div>
            <p v-if="item.description" class="lc-row__desc mb-0">{{ item.description }}</p>
          </div>
          <div class="lc-row__actions">
            <v-btn icon small @click="openCaseModal(item.id)">
              <v-icon size="18" color="#9BA1B5">mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon small @click="deleteItem(item)">
              <v-icon size="18" color="#EC2E2E">mdi-trash-can-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'LibraryCases',
  data: () => ({
    search: '',
    filterCompetence: null,
  }),
  computed: {
    ...mapGetters({
      cases: 'diagnostics/diagnotics/libraryCases',
      loading: 'diagnostics/diagnotics/libraryCasesLoading',
      competencies: 'diagnostics/diagnotics/libraryCompetencies',
    }),
    competenceOptions() {
      return [
        { id: null, name: 'Все компетенции' },
        ...this.competencies.map(c => ({ id: c.id, name: c.name })),
      ];
    },
    filteredCases() {
      let items = this.cases;
      if (this.search.trim()) {
        const s = this.search.toLowerCase();
        items = items.filter(c =>
          c.name.toLowerCase().includes(s) || (c.description && c.description.toLowerCase().includes(s)),
        );
      }
      if (this.filterCompetence !== null) {
        items = items.filter(c => c.competence === this.filterCompetence);
      }
      return items;
    },
  },
  methods: {
    ...mapActions({
      openEditCase: 'diagnostics/diagnotics/openEditCase',
      openCreateCase: 'diagnostics/diagnotics/openCreateCase',
      deleteCaseTemplate: 'diagnostics/diagnotics/deleteCaseTemplate',
    }),
    competenceName(id) {
      const found = this.competencies.find(c => c.id === id);
      return found ? found.name : '';
    },
    openCaseModal(id) {
      if (id) this.openEditCase(id);
      else this.openCreateCase();
    },
    async deleteItem(item) {
      await this.deleteCaseTemplate(item.id);
    },
  },
};
</script>

<style scoped lang="scss">
.lc-search,
.lc-select {
  ::v-deep .v-input__slot {
    border-radius: 8px !important;
    fieldset { border-color: #DEE1EA !important; }
  }
}

.lc-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  overflow: hidden;
}

.lc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #F5F5F5;
  transition: background .12s;

  &:last-child { border-bottom: none; }
  &:hover { background: #FAFAFA; }

  &__main { flex: 1; min-width: 0; }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #25325F;
  }

  &__desc {
    font-size: 13px;
    color: #9BA1B5;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 680px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }
}

.lc-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: #F0F0F0;
  color: #6B759A;
  white-space: nowrap;
}
</style>
